// Assessor name resolution for reports. The report prints the assessor on every row, so this must
// never throw and never blank a row it could have labelled.
//
// ⛔ THE REF COLUMN IS AMBIGUOUS IN THE LIVE DATA, and that is the whole reason this file exists.
//   • 2026-06-25 created parcels.assessor_ref_id as `references public.assessors(id)` and backfilled
//     it with assessors.id values.
//   • 2026-07-19 (versioning) intended it to point at assessor_entities(entity_id) — assessors was
//     rebuilt as a PER-YEAR versioned table, so a single assessors.id is now one YEAR of an assessor,
//     not the assessor.
//   • 2026-07-19 (rebuild) :161-162 then re-declared the FK as `references public.assessors(id)
//     NOT VALID` — so the constraint says `id` while every resolver in the app treats the value as
//     `entity_id` (reports/cards/NoticeProgress.jsx passes it straight to resolveVersionsBulk, which matches on
//     entity_id).
// Result: a ref written before the rebuild holds an assessors.id and matches nothing; a ref written
// after holds an entity_id and resolves. That is the reported "36 of 38 are stale legacy refs".
//
// SO WE TRY BOTH, in order, and fall through rather than failing:
//   1. entity_id  — resolveVersionsBulk, exact year → latest ≤ year → earliest (the app-wide rule)
//   2. assessors.id — direct lookup, for the pre-rebuild refs
//   3. parcels.assessor free text — the legacy column, which is what the screens still display
//   4. null
// Every step is counted, so a caller can log how much of its output is running on the fallback.

import { resolveVersionsBulk } from '../utils/resolveVersion';
import { fetchByIds } from './pagination';

// Normalise the free-text column for grouping. The LA duplicate-row bug was two spellings of one
// assessor under a single assessor_ref_id; text is a label of last resort, never a group key.
export const normaliseAssessorText = (s) => String(s || '').replace(/\s+/g, ' ').trim();

/**
 * Resolve assessor names for a set of parcels at one value year.
 *
 * @param parcels rows carrying { id, assessor_ref_id, assessor }
 * @param year    the value year to resolve the version at
 * @returns {
 *   byParcelId: Map<parcelId, { name, source, row, entityId }>,   // source: 'entity'|'row'|'text'|null
 *   counts:     { entity, row, text, unresolved, total },
 *   unresolvedRefIds: Set<string>                                  // refs that matched nothing
 * }
 */
export async function resolveAssessorsForParcels(parcels, year) {
  const list = parcels || [];
  const byParcelId = new Map();
  const unresolvedRefIds = new Set();
  const counts = { entity: 0, row: 0, text: 0, unresolved: 0, total: list.length };

  const refIds = [...new Set(list.map((p) => p.assessor_ref_id).filter(Boolean))];

  // ── 1. entity_id, with year-version resolution ────────────────────────────────────────────────
  let byEntity = new Map();
  if (refIds.length) {
    try {
      byEntity = await resolveVersionsBulk('assessors', refIds, year);
    } catch (e) {
      // Never throw: a report labels rows, and an unreachable assessors table must degrade to the
      // free-text column rather than blanking the whole report.
      console.warn('[reports/assessor] entity resolution failed — falling back to text:', e?.message || e);
    }
  }

  // ── 2. assessors.id, for the pre-rebuild refs that step 1 could not match ─────────────────────
  const stillMissing = refIds.filter((id) => !byEntity.has(id));
  const byRowId = new Map();
  if (stillMissing.length) {
    try {
      const rows = await fetchByIds('assessors', 'id, entity_id, name, state, county, value_year', 'id', stillMissing);
      rows.forEach((r) => byRowId.set(r.id, r));
    } catch (e) {
      console.warn('[reports/assessor] legacy id lookup failed — falling back to text:', e?.message || e);
    }
  }

  // ── 3/4. assemble ─────────────────────────────────────────────────────────────────────────────
  list.forEach((p) => {
    const ref = p.assessor_ref_id;
    const viaEntity = ref ? byEntity.get(ref) : null;
    if (viaEntity && viaEntity.name) {
      byParcelId.set(p.id, { name: viaEntity.name, source: 'entity', row: viaEntity, entityId: ref });
      counts.entity += 1;
      return;
    }
    const viaRow = ref ? byRowId.get(ref) : null;
    if (viaRow && viaRow.name) {
      byParcelId.set(p.id, { name: viaRow.name, source: 'row', row: viaRow, entityId: viaRow.entity_id || null });
      counts.row += 1;
      if (ref) unresolvedRefIds.add(ref);      // resolved, but NOT as an entity — the ref is legacy
      return;
    }
    if (ref) unresolvedRefIds.add(ref);
    const text = normaliseAssessorText(p.assessor);
    if (text) {
      byParcelId.set(p.id, { name: text, source: 'text', row: null, entityId: null });
      counts.text += 1;
      return;
    }
    byParcelId.set(p.id, { name: null, source: null, row: null, entityId: null });
    counts.unresolved += 1;
  });

  if (counts.row || counts.text || counts.unresolved) {
    console.info(
      `[reports/assessor] ${year}: ${counts.entity} by entity, ${counts.row} by legacy assessors.id, ` +
      `${counts.text} by free text, ${counts.unresolved} unlabelled (of ${counts.total}). ` +
      `${unresolvedRefIds.size} ref id(s) did not resolve as an entity.`
    );
  }
  return { byParcelId, counts, unresolvedRefIds };
}

// Convenience for a report that just wants a name and does not care how it was found.
export const assessorName = (resolved, parcelId, fallback = '—') =>
  (resolved?.byParcelId?.get(parcelId)?.name) || fallback;
